import { memo, useState } from 'react';
import {
    ComposableMap,
    Geographies,
    Geography,
    ZoomableGroup,
} from 'react-simple-maps';
import countryMappingData from '../data/countryMapping.json';

const geoUrl = "/countries-geo.json"; // Will use local geographic data file

const WorldMap = ({ geographicData, onCountryClick }) => {
    const [tooltipContent, setTooltipContent] = useState("");
    const [position, setPosition] = useState({ coordinates: [0, 0], zoom: 1 });

    // Import bidirectional country mapping from external JSON file
    const backendToFrontend = countryMappingData.backendToFrontend || {};
    const frontendToBackend = countryMappingData.frontendToBackend || {};
    const countryAliases = countryMappingData.aliases || {};

    // Create alias lookup for additional flexibility
    const aliasLookup = {};
    Object.entries(countryAliases).forEach(([country, aliases]) => {
        // Ensure aliases is an array before using forEach
        if (Array.isArray(aliases)) {
            aliases.forEach(alias => {
                aliasLookup[alias.toLowerCase()] = country;
            });
        }
    });

    // Calculate max views for color intensity
    const maxViews = Math.max(...(geographicData || []).map(item => item.views || 0));

    // Get color intensity based on views
    const getColorIntensity = (views) => {
        if (!views || maxViews === 0) return 0;
        return Math.min(views / maxViews, 1);
    };

    // Normalize country name for better matching
    const normalizeCountryName = (name) => {
        if (!name) return '';
        return name
            .toLowerCase()
            .trim()
            .replace(/\s+/g, ' ') // normalize whitespace
            .replace(/[^\w\s]/g, '') // remove special characters
            .replace(/\brepublic\b/g, 'rep') // standardize "republic" -> "rep"
            .replace(/\bdemocratic\b/g, 'dem') // standardize "democratic" -> "dem"
            .replace(/\bunited\s+states\s+of\s+america\b/g, 'united states')
            .replace(/\bunited\s+kingdom\b/g, 'uk')
            .replace(/\bsouth\s+korea\b/g, 'korea south')
            .replace(/\bnorth\s+korea\b/g, 'korea north');
    };

    // Advanced bidirectional country matching with comprehensive mapping
    const findCountryData = (geoCountryName) => {
        if (!geographicData || !geoCountryName) return null;

        // 1. Try exact match first (fastest path)
        let data = geographicData.find(item =>
            item.country === geoCountryName ||
            item.country?.toLowerCase() === geoCountryName?.toLowerCase()
        );
        if (data) return data;

        // 2. Try frontend-to-backend mapping (GeoJSON name → Backend name)
        const backendName = frontendToBackend[geoCountryName];
        if (backendName) {
            data = geographicData.find(item =>
                item.country === backendName ||
                item.country?.toLowerCase() === backendName?.toLowerCase()
            );
            if (data) return data;
        }

        // 3. Try backend-to-frontend mapping (Backend name → GeoJSON name)
        const frontendName = backendToFrontend[geoCountryName];
        if (frontendName) {
            data = geographicData.find(item =>
                item.country === frontendName ||
                item.country?.toLowerCase() === frontendName?.toLowerCase()
            );
            if (data) return data;
        }

        // 4. Try alias lookup
        const aliasMatch = aliasLookup[geoCountryName.toLowerCase()];
        if (aliasMatch) {
            data = geographicData.find(item =>
                item.country === aliasMatch ||
                item.country?.toLowerCase() === aliasMatch?.toLowerCase()
            );
            if (data) return data;
        }

        // 5. Try case-insensitive mapping lookups
        const geoLower = geoCountryName.toLowerCase();
        for (const [frontend, backend] of Object.entries(frontendToBackend)) {
            if (frontend.toLowerCase() === geoLower) {
                data = geographicData.find(item =>
                    item.country === backend ||
                    item.country?.toLowerCase() === backend?.toLowerCase()
                );
                if (data) return data;
            }
        }

        // 6. Try normalized matching for fuzzy matches
        const normalizedGeoName = normalizeCountryName(geoCountryName);
        data = geographicData.find(item => {
            const normalizedDataName = normalizeCountryName(item.country);
            return normalizedDataName === normalizedGeoName ||
                normalizedDataName.includes(normalizedGeoName) ||
                normalizedGeoName.includes(normalizedDataName);
        });
        if (data) return data;

        // 7. Try partial word matching (useful for abbreviated names)
        const geoWords = geoCountryName.toLowerCase().split(/\s+/);
        data = geographicData.find(item => {
            if (!item.country) return false;
            const dataWords = item.country.toLowerCase().split(/\s+/);
            return geoWords.some(geoWord =>
                dataWords.some(dataWord =>
                    dataWord.includes(geoWord) || geoWord.includes(dataWord)
                )
            );
        });

        // Note: Debug logging removed for production - all countries should now be mapped

        return data;
    };

    // Get color for country based on traffic views
    const getCountryColor = (views) => {
        if (!views) return "#e2e8f0"; // Slightly darker gray for no data - better visibility

        const intensity = getColorIntensity(views);
        // Use a more vibrant blue gradient from medium to dark
        const alpha = 0.4 + (intensity * 0.5); // Range from 0.4 to 0.9 for better visibility
        return `rgba(37, 99, 235, ${alpha})`; // Darker blue base color
    };

    const handleZoomIn = () => {
        if (position.zoom >= 4) return;
        setPosition(pos => ({ ...pos, zoom: pos.zoom * 2 }));
    };

    const handleZoomOut = () => {
        if (position.zoom <= 1) return;
        setPosition(pos => ({ ...pos, zoom: pos.zoom / 2 }));
    };

    const handleMoveEnd = (position) => {
        setPosition(position);
    };

    return (
        <div className="relative bg-slate-50 rounded-lg border border-gray-300 p-4 shadow-inner h-full flex flex-col overflow-hidden">
            {/* Map Controls */}
            <div className="absolute top-6 right-6 z-10 flex flex-col gap-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <button
                    onClick={handleZoomIn}
                    className="w-9 h-9 bg-white hover:bg-blue-50 flex items-center justify-center text-gray-700 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-b border-gray-100 last:border-b-0"
                    disabled={position.zoom >= 4}
                    title="Zoom In"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                </button>
                <button
                    onClick={handleZoomOut}
                    className="w-9 h-9 bg-white hover:bg-blue-50 flex items-center justify-center text-gray-700 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={position.zoom <= 1}
                    title="Zoom Out"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
                    </svg>
                </button>
            </div>

            {/* Legend */}
            <div className="absolute bottom-6 left-6 z-10 bg-white border border-gray-200 rounded-lg p-4 shadow-lg">
                <div className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    Traffic Intensity
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-gray-600">Low</span>
                    <div className="flex gap-1">
                        {[0.4, 0.55, 0.7, 0.9].map((alpha, idx) => (
                            <div
                                key={idx}
                                className="w-5 h-4 border border-gray-300 rounded-sm shadow-sm"
                                style={{ backgroundColor: `rgba(37, 99, 235, ${alpha})` }}
                            />
                        ))}
                    </div>
                    <span className="text-xs font-medium text-gray-600">High</span>
                </div>
            </div>

            {/* World Map */}
            <div className="w-full flex-1 relative overflow-hidden" style={{ minHeight: '400px', maxHeight: '400px' }}>
                <ComposableMap
                    projectionConfig={{
                        rotate: [-10, 0, 0],
                        scale: 160 // Slightly larger for better visibility
                    }}
                    style={{
                        width: "100%",
                        height: "100%",
                        backgroundColor: "transparent",
                        maxWidth: "100%",
                        maxHeight: "100%"
                    }}
                >
                    <ZoomableGroup
                        zoom={position.zoom}
                        center={position.coordinates}
                        onMoveEnd={handleMoveEnd}
                    >
                        <Geographies geography={geoUrl}>
                            {({ geographies }) =>
                                geographies.map((geo) => {
                                    const geoCountryName = geo.properties.name;
                                    const data = findCountryData(geoCountryName);

                                    return (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            onMouseEnter={() => {
                                                if (data) {
                                                    const displayName = data.country || geoCountryName;
                                                    setTooltipContent(
                                                        `${displayName}: ${data.views} views (${data.uniqueVisitors || 0} unique visitors)`
                                                    );
                                                } else {
                                                    setTooltipContent(`${geoCountryName}: No data`);
                                                }
                                            }}
                                            onMouseLeave={() => {
                                                setTooltipContent("");
                                            }}
                                            onClick={() => {
                                                if (data && onCountryClick) {
                                                    onCountryClick(data);
                                                }
                                            }}
                                            style={{
                                                default: {
                                                    fill: data ? getCountryColor(data.views) : "#e2e8f0",
                                                    stroke: "#9ca3af", // Darker stroke for better visibility
                                                    strokeWidth: 0.8, // Slightly thicker strokes
                                                    outline: "none",
                                                },
                                                hover: {
                                                    fill: data ? "#1e40af" : "#cbd5e1", // Darker hover color
                                                    stroke: "#374151", // Much darker stroke on hover
                                                    strokeWidth: 1.2, // Thicker stroke on hover
                                                    outline: "none",
                                                    cursor: data ? "pointer" : "default",
                                                },
                                                pressed: {
                                                    fill: "#1e3a8a", // Even darker when pressed
                                                    stroke: "#1f2937",
                                                    strokeWidth: 1.2,
                                                    outline: "none",
                                                },
                                            }}
                                        />
                                    );
                                })
                            }
                        </Geographies>
                    </ZoomableGroup>
                </ComposableMap>
            </div>

            {/* Tooltip */}
            {tooltipContent && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white text-sm px-3 py-2 rounded-lg shadow-xl z-20 whitespace-nowrap border border-slate-600 backdrop-blur-sm">
                    <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span>{tooltipContent}</span>
                    </div>
                </div>
            )}

            {/* No Data Message */}
            {(!geographicData || geographicData.length === 0) && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-75">
                    <div className="text-center">
                        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
                            </svg>
                        </div>
                        <p className="text-sm text-gray-500 font-medium">No geographic data available</p>
                        <p className="text-xs text-gray-400 mt-1">Widget views will appear here once collected</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default memo(WorldMap);
